import React, { useState, useEffect } from 'react';
import { ClockIcon, StarIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { ScanHistoryItem, ApiProvider } from '../types';
import { scanService } from '../services/scanService';
import { statsEmitter } from '../utils/statsEmitter';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import ResultCard from '../components/ResultCard';
import { ListSkeleton } from '../components/Skeleton';
import PullToRefresh from '../components/PullToRefresh';

const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<ScanHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<ScanHistoryItem | null>(null);
  const { showToast } = useToast();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    loadHistory();
  }, [isAuthenticated]);

  const loadHistory = async () => {
    if (!isAuthenticated) {
      setHistory([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const scanHistory = await scanService.getScanHistory();
      setHistory(scanHistory);
    } catch (error) {
      console.error('Failed to load history:', error);
      showToast('error', 'Failed to load scan history');
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFavorite = async (id: string) => {
    if (!isAuthenticated) {
      showToast('error', 'Please sign in to favorite items');
      return;
    }

    try {
      await scanService.toggleSaved(id);
      setHistory(prev =>
        prev.map(item =>
          item.id === id ? { ...item, isFavorite: !item.isFavorite } : item
        )
      );
      const item = history.find(h => h.id === id);
      showToast('success', item?.isFavorite ? 'Removed from favorites' : 'Added to favorites');
      
      // Emit stats refresh to update favorite items count
      statsEmitter.emit();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      showToast('error', 'Failed to update favorite status');
    }
  };

  const deleteItem = async (id: string) => {
    if (!isAuthenticated) {
      showToast('error', 'Please sign in to delete items');
      return;
    }

    try {
      await scanService.deleteScan(id);
      setHistory(prev => prev.filter(item => item.id !== id));
      if (selectedItem?.id === id) {
        setSelectedItem(null);
      }
      showToast('success', 'Scan deleted successfully');
      
      // Emit stats refresh to update total scans count
      statsEmitter.emit();
    } catch (error) {
      console.error('Failed to delete item:', error);
      showToast('error', 'Failed to delete scan');
    }
  };

  if (selectedItem) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900">
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedItem(null)}
              className="inline-flex items-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
            >
              ← Back to History
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Analysis Details
            </h1>
          </div>
          <ResultCard result={selectedItem} />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900">
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-6">
              <ClockIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Scan History
            </h1>
          </div>
          <ListSkeleton items={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900">
      <PullToRefresh onRefresh={loadHistory}>
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-6">
              <ClockIcon className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Scan History
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              View and manage all your previous AI-powered item analyses
            </p>
          </div>

          {history.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-700 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <ClockIcon className="h-12 w-12 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                No recorded analyses yet
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Save an analysis or add it to inventory to see it here.
              </p>
              <button
                onClick={() => window.location.href = '/camera'}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Start Scanning
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-tour="tour-history-list">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
                      {item.value}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {new Date(item.timestamp).toLocaleDateString()} • {item.apiProvider}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setSelectedItem(item)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 space-x-2"
                      >
                        <EyeIcon className="h-4 w-4" />
                        <span>View</span>
                      </button>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleFavorite(item.id)}
                          className="p-2 text-gray-400 hover:text-yellow-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                        >
                          {item.isFavorite ? (
                            <StarIconSolid className="h-5 w-5 text-yellow-500" />
                          ) : (
                            <StarIcon className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => deleteItem(item.id)}
                          className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </PullToRefresh>
    </div>
  );
};

export default HistoryPage;
